import { useMemo, useState } from 'react';
import {
  ExampleCallout,
  FieldLabel,
  InfoSection,
  PanelTitle,
  ResultsEmpty,
  ToolPageHeading,
  ToolTwoColumn,
  fmtPct,
  parseNum,
  toolInputClass,
} from './toolShared';

const DEFAULTS = { noi: '', value: '' };

export function ToolCapRate() {
  const [v, setV] = useState(DEFAULTS);

  const capRate = useMemo(() => {
    const noi = parseNum(v.noi);
    const val = parseNum(v.value);
    if (val <= 0) return null;
    return (noi / val) * 100;
  }, [v]);

  return (
    <div className="space-y-8">
      <ToolPageHeading
        title="Cap rate calculator"
        subtitle="Calculate the capitalization rate to evaluate your property investment potential."
      />

      <InfoSection title="Understanding cap rate">
        <p>
          <strong className="text-[var(--home-text-on-dark)]">Cap rate</strong> expresses net operating income (NOI) as a
          percentage of value: useful for comparing deals on an unlevered basis.
        </p>
        <p className="font-mono text-sm text-[var(--home-miami-blue)]">
          Cap rate = (NOI ÷ Property value) × 100
        </p>
        <ul className="list-disc space-y-1 pl-5 text-[var(--home-text-on-dark)]/90">
          <li>
            <strong>Net operating income (NOI)</strong> — Effective gross income minus operating expenses (excluding debt
            service).
          </li>
          <li>
            <strong>Property value</strong> — Purchase price or your opinion of value for this calculation.
          </li>
        </ul>
        <ExampleCallout>
          Example: $60,000 NOI ÷ $800,000 value = <strong className="text-[var(--home-text-on-dark)]">7.5%</strong> cap rate.
        </ExampleCallout>
      </InfoSection>

      <ToolTwoColumn
        onReset={() => setV(DEFAULTS)}
        left={
          <>
            <PanelTitle>Property details</PanelTitle>
            <div className="space-y-4">
              <div className="space-y-1.5">
                <FieldLabel htmlFor="cr-noi" tip="Annual net operating income before debt." required>
                  Net operating income (NOI)
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="cr-noi"
                    type="text"
                    inputMode="decimal"
                    value={v.noi}
                    onChange={(e) => setV((s) => ({ ...s, noi: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <FieldLabel htmlFor="cr-val" tip="Price or value to capitalize." required>
                  Property value
                </FieldLabel>
                <div className="flex rounded-xl border border-white/12 bg-white/[0.08] shadow-inner shadow-black/25 backdrop-blur-sm focus-within:border-[var(--home-miami-blue)] focus-within:ring-2 focus-within:ring-[var(--home-miami-blue)]/30">
                  <span className="flex items-center pl-3 font-body text-sm text-[var(--home-muted-on-dark)]">$</span>
                  <input
                    id="cr-val"
                    type="text"
                    inputMode="decimal"
                    value={v.value}
                    onChange={(e) => setV((s) => ({ ...s, value: e.target.value }))}
                    className={`${toolInputClass} border-0 bg-transparent pl-1 shadow-none ring-0 focus:ring-0`}
                    placeholder="0.00"
                  />
                </div>
              </div>
            </div>
          </>
        }
        right={
          <>
            <PanelTitle>Cap rate results</PanelTitle>
            {capRate === null ? (
              <ResultsEmpty message="Enter NOI and property value to calculate cap rate." />
            ) : (
              <div className="flex min-h-[220px] flex-col justify-center space-y-2">
                <p className="font-body text-xs uppercase tracking-wide text-[var(--home-muted-on-dark)]">Cap rate</p>
                <p className="font-display text-3xl font-bold text-[var(--home-text-on-dark)]">{fmtPct(capRate)}</p>
              </div>
            )}
          </>
        }
      />
    </div>
  );
}
